import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  Select,
  MenuItem,
  Switch,
  Chip,
  IconButton,
  Alert,
  Typography,
  TextField,
  Checkbox,
  ListItemText,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import { useTranslation } from "react-i18next";
import { useGetToken } from "../../hooks/useGetToken";
import StyledButtons from "../../styledComponents/styledButtons";
import Loading from "../Loading";
import { CONSTANTS } from "../../CONSTANTS";
import {
  fetchRegionData,
  fetchFrontlineData,
  fetchCountryData,
} from "../../services/TrainingsHeaderViewService";
import {
  getOnboardConfig,
  saveOnboardConfig,
  BaseFieldDefinition,
  BusinessTypeOption,
  CountryOption,
  CustomFieldDefinition,
  CustomFieldType,
  FieldAppliesTo,
  FIELD_APPLIES_TO_VALUES,
  supportsUniqueness,
} from "../../services/countryConfigService";

/** Reference data is language-tagged; the builder is an English-only screen. */
const DEFAULT_LANG = "en";

/** A region or frontline option, normalised out of the reference endpoints. */
interface HierarchyOption {
  id: number;
  label: string;
}

/**
 * The reference endpoints return prefixed column aliases - `region_id` /
 * `region_name` / `region_code`, and the frontline equivalents - not `id` and
 * `name`. Reading them as `id`/`name` yields undefined, which renders as a
 * dropdown of blank rows.
 *
 * `*_code` is the translated label and `*_name` the raw value; when a
 * translation is missing the API hands back the key itself, so fall back to the
 * name rather than showing "regions.something".
 */
const toHierarchyOptions = (
  rows: any,
  prefix: "region" | "frontline",
): HierarchyOption[] => {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r: any) => r && r[`${prefix}_id`] != null)
    .map((r: any) => {
      const name = ("" + (r[`${prefix}_name`] ?? "")).trim();
      const code = ("" + (r[`${prefix}_code`] ?? "")).trim();
      const usable = code && code.indexOf(`${prefix}s.`) !== 0 ? code : name;
      return { id: Number(r[`${prefix}_id`]), label: usable || name };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

const FIELD_TYPES: CustomFieldType[] = [
  "text",
  "number",
  "date",
  "dropdown",
  "checkbox",
];

/** Mirrors COLUMN_NAME_PATTERN in the API (helpers/customFields/identifierSafety.ts). */
const COLUMN_NAME_PATTERN = /^[a-z][a-z0-9_]{0,60}$/;

/**
 * Mirrors MAX_FIELD_NAME_LENGTH in the API. The name the admin types becomes a
 * database column name, and a unique field also needs a `uq_` prefixed index,
 * which has to fit inside MySQL's 64-character identifier limit.
 */
const MAX_FIELD_NAME_LENGTH = 61;

/**
 * Mirrors RESERVED_IDENTIFIERS in the API. Checked client-side so the admin gets
 * a message against the field instead of a generic save failure.
 */
const RESERVED_COLUMN_NAMES = new Set<string>([
  "id", "user_id", "country_id", "created_date", "modified_date",
  "select", "insert", "update", "delete", "drop", "table", "from", "where",
  "order", "group", "index", "key", "primary", "foreign", "constraint",
  "default", "null", "and", "or", "not",
]);

/** Largest char limit the API accepts for a text field (MAX_TEXT_LIMIT). */
const MAX_CHAR_LIMIT = 4000;

/**
 * Layout tokens shared with the bulk onboarding grid so the two admin overlays
 * read as one system: aligned columns, a sticky header and footer, and scrolling
 * confined to the body rather than the whole dialog.
 */
const ACCENT = CONSTANTS.COLORS.BRAND_BLUE;
const CHIP_BG = CONSTANTS.COLORS.BUTTON_GREY;
const CHIP_TEXT = CONSTANTS.COLORS.BUTTON_BLUE;
const BORDER = "#e4e7ec";
const ROW_BORDER = "#eceef2";
const HEAD_BG = "#f9fafb";
const MUTED = "#667085";

/** Column templates - base and custom rows share the first two column widths. */
const BASE_COLS = "minmax(200px, 1.6fr) 120px 120px 140px";
const CUSTOM_COLS =
  "minmax(160px, 1.3fr) 100px minmax(110px, 1fr) 116px minmax(130px, 1fr) 84px 74px 44px";

const columnHeadSx = {
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  color: MUTED,
};

const sectionTitleSx = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#101828",
};

interface CountryOnboardingProps {
  open: boolean;
  onClose: () => void;
}

/** A custom field row held in local state while the admin edits the config. */
interface EditableCustomField extends CustomFieldDefinition {
  /** Added in this session and not yet persisted - removing it just drops it. */
  isNew?: boolean;
}

/** Per-country visibility / mandatory state for a base field. */
interface BaseFieldState {
  isVisible: boolean;
  isMandatory: boolean;
}

interface FieldDraft {
  displayName: string;
  fieldType: CustomFieldType;
  isMandatory: boolean;
  isUnique: boolean;
  charLimit: string;
  fieldOptions: string;
  appliesTo: FieldAppliesTo;
  businessTypeIds: number[];
}

const EMPTY_DRAFT: FieldDraft = {
  displayName: "",
  fieldType: "text",
  isMandatory: false,
  // Off by default: a uniqueness constraint is an explicit decision, and one
  // added by accident would start rejecting perfectly valid users.
  isUnique: false,
  charLimit: "",
  fieldOptions: "",
  // Default to "all" so a new field is never silently narrowed.
  appliesTo: "all",
  businessTypeIds: [],
};

/**
 * Super-admin "Onboard / Configure Country" field builder.
 *
 * Loads the base field catalog (mandatory-locked fields are non-configurable;
 * optional ones can be hidden or made mandatory per country), lets the admin add,
 * edit and remove custom fields on demand, and persists everything via
 * Save & Onboard.
 */
const CountryOnboarding = ({
  open,
  onClose,
}: CountryOnboardingProps): JSX.Element => {
  const { t } = useTranslation();
  const [getToken] = useGetToken();

  const [regionList, setRegionList] = useState<HierarchyOption[]>([]);
  const [frontlineList, setFrontlineList] = useState<HierarchyOption[]>([]);
  const [selectedRegionId, setSelectedRegionId] = useState<number | "">("");
  const [selectedFrontlineId, setSelectedFrontlineId] = useState<number | "">("");
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [frontlinesLoading, setFrontlinesLoading] = useState(false);
  const [countryList, setCountryList] = useState<CountryOption[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<number | "">("");
  const [catalog, setCatalog] = useState<BaseFieldDefinition[]>([]);
  const [businessTypes, setBusinessTypes] = useState<BusinessTypeOption[]>([]);
  const [baseState, setBaseState] = useState<Record<string, BaseFieldState>>({});
  const [customFields, setCustomFields] = useState<EditableCustomField[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  /** Fields the API archived on the last save, surfaced so removals are visible. */
  const [archivedFields, setArchivedFields] = useState<string[]>([]);

  const [draft, setDraft] = useState<FieldDraft>(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState("");

  const resetDraft = () => setDraft(EMPTY_DRAFT);

  /**
   * The alerts sit at the top of the scrolling body while "Save & Onboard"
   * lives in the sticky footer, so an admin who has scrolled down to the field
   * list would never see the outcome of their own save. Bring it into view.
   */
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!error && !success && archivedFields.length === 0) return;
    const body = bodyRef.current;
    if (body) body.scrollTo({ top: 0, behavior: "smooth" });
  }, [error, success, archivedFields]);

  const loadConfig = async (countryId: number) => {
    setLoading(true);
    setError("");
    setSuccess("");
    setArchivedFields([]);
    try {
      const token = await getToken();
      const config = await getOnboardConfig(countryId, token);
      const catalogList = config.baseFieldCatalog || [];
      setCatalog(catalogList);
      setBusinessTypes(config.businessTypes || []);

      // Merge per-country overrides onto catalog defaults.
      const overridesByKey: Record<
        string,
        { isVisible: boolean; isMandatory: boolean }
      > = {};
      (config.baseFieldOverrides || []).forEach((o) => {
        overridesByKey[o.fieldKey] = o;
      });
      const nextBaseState: Record<string, BaseFieldState> = {};
      catalogList.forEach((f) => {
        const o = overridesByKey[f.fieldKey];
        nextBaseState[f.fieldKey] = {
          isVisible: o ? !!o.isVisible : !!f.defaultVisible,
          isMandatory: o ? !!o.isMandatory : !!f.defaultMandatory,
        };
      });
      setBaseState(nextBaseState);
      setCustomFields(
        (config.customFields || []).map((c) => ({
          ...c,
          status: c.status || "active",
        })),
      );
    } catch (e) {
      console.error("Error loading onboard config:", e);
      setError(t("countryOnboarding.errors.loadFailed"));
      setCatalog([]);
      setBaseState({});
      setCustomFields([]);
    } finally {
      setLoading(false);
    }
  };

  // Load the selectable countries when the dialog opens. Fetched here rather
  // than passed in, so the builder does not depend on where the admin happens
  // to be in the table's Business Type / Area / Frontline drill-down.
  /**
   * Region -> Frontline -> Country, the same drill-down the rest of the admin
   * tool uses. A flat list of every country an admin can configure ignores the
   * structure they think in, and for a wide-scope admin it is a long scroll with
   * no grouping.
   *
   * Each level is fetched rather than filtered client-side, so each one is
   * scoped by the caller's own access on the server - the same endpoints the
   * installer form drills through.
   */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadRegions = async () => {
      setRegionsLoading(true);
      try {
        const token = await getToken();
        const regions = await fetchRegionData(token, DEFAULT_LANG);
        if (!cancelled) setRegionList(toHierarchyOptions(regions, "region"));
      } catch (e) {
        console.error("Error loading regions:", e);
        if (!cancelled) {
          setRegionList([]);
          setError(t("countryOnboarding.errors.countriesFailed"));
        }
      } finally {
        if (!cancelled) setRegionsLoading(false);
      }
    };
    loadRegions();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Choosing a region invalidates everything below it, so the frontline and
  // country both reset - leaving a stale country selected would keep the
  // builder showing a configuration the admin is no longer looking at.
  useEffect(() => {
    setFrontlineList([]);
    setCountryList([]);
    setSelectedFrontlineId("");
    setSelectedCountryId("");
    if (selectedRegionId === "") return;
    let cancelled = false;
    const loadFrontlines = async () => {
      setFrontlinesLoading(true);
      try {
        const token = await getToken();
        const frontlines = await fetchFrontlineData(
          token,
          String(selectedRegionId),
          DEFAULT_LANG,
        );
        if (!cancelled)
          setFrontlineList(toHierarchyOptions(frontlines, "frontline"));
      } catch (e) {
        console.error("Error loading frontlines:", e);
        if (!cancelled) setFrontlineList([]);
      } finally {
        if (!cancelled) setFrontlinesLoading(false);
      }
    };
    loadFrontlines();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegionId]);

  useEffect(() => {
    setCountryList([]);
    setSelectedCountryId("");
    if (selectedFrontlineId === "") return;
    let cancelled = false;
    const loadCountries = async () => {
      setCountriesLoading(true);
      try {
        const token = await getToken();
        const countries = await fetchCountryData(
          token,
          String(selectedFrontlineId),
          DEFAULT_LANG,
        );
        if (!cancelled) {
          setCountryList(
            (Array.isArray(countries) ? countries : []).sort(
              (a: CountryOption, b: CountryOption) =>
                (a.name || "").localeCompare(b.name || ""),
            ),
          );
        }
      } catch (e) {
        console.error("Error loading countries:", e);
        if (!cancelled) {
          setCountryList([]);
          setError(t("countryOnboarding.errors.countriesFailed"));
        }
      } finally {
        if (!cancelled) setCountriesLoading(false);
      }
    };
    loadCountries();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFrontlineId]);

  useEffect(() => {
    if (selectedCountryId !== "") {
      loadConfig(selectedCountryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryId]);

  const toggleBase = (fieldKey: string, prop: keyof BaseFieldState) => {
    setBaseState((prev) => {
      const current = prev[fieldKey] || { isVisible: true, isMandatory: false };
      const updated: BaseFieldState = { ...current, [prop]: !current[prop] };
      // Hidden fields cannot be mandatory.
      if (prop === "isVisible" && !updated.isVisible) {
        updated.isMandatory = false;
      }
      return { ...prev, [fieldKey]: updated };
    });
  };

  /**
   * Build a safe snake_case column name from the display name.
   *
   * Case and punctuation are the admin's to use freely - they are normalised
   * here rather than rejected. Length is the one thing they have to keep to,
   * because the result becomes a real column name; it is deliberately NOT
   * truncated, so an over-long name is reported instead of silently becoming a
   * different column than the name suggests.
   */
  const toColumnName = (name: string): string => {
    let col = (name || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^[0-9]+/, "");
    if (!col) return "";
    if (!/^[a-z]/.test(col)) col = `f_${col}`;
    return col;
  };

  /**
   * Validate a derived column name against the same rules the API enforces, so
   * failures surface next to the field rather than as a generic save error.
   */
  const validateColumnName = (columnName: string): string => {
    if (!columnName || !COLUMN_NAME_PATTERN.test(columnName)) {
      return t("countryOnboarding.errors.invalidName");
    }
    if (RESERVED_COLUMN_NAMES.has(columnName)) {
      return t("countryOnboarding.errors.reservedName");
    }
    return "";
  };

  /** Parse and validate a char limit. Only meaningful for text fields. */
  const parseCharLimit = (
    raw: string,
  ): { value: number | null; error: string } => {
    if (!raw) return { value: null, error: "" };
    const parsed = Number(raw);
    if (!Number.isInteger(parsed) || parsed <= 0 || parsed > MAX_CHAR_LIMIT) {
      return {
        value: null,
        error: t("countryOnboarding.errors.invalidCharLimit"),
      };
    }
    return { value: parsed, error: "" };
  };

  const parseOptions = (raw: string): string[] =>
    raw
      .split(",")
      .map((o) => o.trim())
      .filter(Boolean);

  const handleAddField = () => {
    setDraftError("");
    const displayName = draft.displayName.trim();
    if (!displayName) {
      setDraftError(t("countryOnboarding.errors.nameRequired"));
      return;
    }
    if (displayName.length > MAX_FIELD_NAME_LENGTH) {
      setDraftError(
        t("countryOnboarding.errors.nameTooLong", {
          max: MAX_FIELD_NAME_LENGTH,
        }),
      );
      return;
    }
    // Checked after the display name because a name made only of punctuation
    // leaves nothing to derive a column from, which is a different problem.
    const columnName = toColumnName(displayName);
    const nameError = validateColumnName(columnName);
    if (nameError) {
      setDraftError(nameError);
      return;
    }
    if (catalog.some((f) => f.fieldKey === columnName)) {
      setDraftError(t("countryOnboarding.errors.duplicateName"));
      return;
    }

    // A field with this name may already exist but be archived (removed earlier
    // in this session, or archived by a previous save). Restore it rather than
    // pushing a second row - the API rejects duplicate column names outright.
    const existing = customFields.find((f) => f.columnName === columnName);
    if (existing && existing.status !== "archived") {
      setDraftError(t("countryOnboarding.errors.duplicateName"));
      return;
    }

    let fieldOptions: string[] | null = null;
    if (draft.fieldType === "dropdown") {
      fieldOptions = parseOptions(draft.fieldOptions);
      if (fieldOptions.length === 0) {
        setDraftError(t("countryOnboarding.errors.optionsRequired"));
        return;
      }
    }

    // charLimit only backs a text column; number is DECIMAL(20,4) server-side.
    let charLimit: number | null = null;
    if (draft.fieldType === "text") {
      const parsed = parseCharLimit(draft.charLimit);
      if (parsed.error) {
        setDraftError(parsed.error);
        return;
      }
      charLimit = parsed.value;
    }

    if (existing) {
      setCustomFields((prev) =>
        prev.map((f) =>
          f.columnName === columnName
            ? {
                ...f,
                displayName,
                fieldType: draft.fieldType,
                isMandatory: draft.isMandatory,
                // A type change can take the field out of the set that supports
                // uniqueness, so the flag is re-evaluated rather than carried.
                isUnique:
                  draft.isUnique && supportsUniqueness(draft.fieldType),
                charLimit,
                fieldOptions,
                appliesTo: draft.appliesTo,
                businessTypeIds: draft.businessTypeIds,
                status: "active",
              }
            : f,
        ),
      );
    } else {
      setCustomFields((prev) => [
        ...prev,
        {
          columnName,
          displayName,
          fieldType: draft.fieldType,
          isMandatory: draft.isMandatory,
          isUnique: draft.isUnique && supportsUniqueness(draft.fieldType),
          charLimit,
          fieldOptions,
          appliesTo: draft.appliesTo,
          businessTypeIds: draft.businessTypeIds,
          displayOrder: prev.length + 1,
          status: "active",
          isNew: true,
        },
      ]);
    }
    resetDraft();
  };

  /** Update one property of an existing custom field row. */
  const updateCustomField = (
    columnName: string,
    patch: Partial<EditableCustomField>,
  ) => {
    setCustomFields((prev) =>
      prev.map((f) => (f.columnName === columnName ? { ...f, ...patch } : f)),
    );
  };

  const handleRemoveField = (columnName: string) => {
    setCustomFields((prev) =>
      prev
        .map((f) => {
          if (f.columnName !== columnName) return f;
          // Newly added (unsaved) fields are dropped; persisted ones are archived
          // so the column and its stored values survive.
          return f.isNew ? null : { ...f, status: "archived" as const };
        })
        .filter((f): f is EditableCustomField => f !== null),
    );
  };

  const handleSave = async () => {
    if (selectedCountryId === "") return;
    setSaving(true);
    setError("");
    setSuccess("");
    setArchivedFields([]);
    try {
      const token = await getToken();
      const baseFieldOverrides = catalog
        .filter((f) => f.configurable)
        .map((f) => ({
          fieldKey: f.fieldKey,
          isVisible: baseState[f.fieldKey]?.isVisible ?? f.defaultVisible,
          isMandatory: baseState[f.fieldKey]?.isMandatory ?? f.defaultMandatory,
          displayOrder: f.displayOrder,
        }));
      const payloadCustomFields = customFields.map((f, index) => ({
        columnName: f.columnName,
        displayName: f.displayName,
        fieldType: f.fieldType,
        isMandatory: !!f.isMandatory,
        // Guarded on the type as well: the API rejects the flag on a type that
        // cannot carry it, so a field switched to date after being marked
        // unique would otherwise fail the whole save.
        isUnique: !!f.isUnique && supportsUniqueness(f.fieldType),
        charLimit: f.fieldType === "text" ? f.charLimit ?? null : null,
        fieldOptions: f.fieldType === "dropdown" ? f.fieldOptions ?? null : null,
        displayOrder: f.displayOrder ?? index + 1,
        status: f.status || "active",
        appliesTo: f.appliesTo ?? "all",
        businessTypeIds: f.businessTypeIds ?? [],
      }));
      const result = await saveOnboardConfig(
        selectedCountryId,
        { baseFieldOverrides, customFields: payloadCustomFields },
        token,
      );
      // Reload first, then announce: loadConfig resets the banner state, so
      // setting the outcome before it would wipe the message and, worse, the
      // archived-field warning that exists so a removal is never silent.
      await loadConfig(selectedCountryId);
      setSuccess(t("countryOnboarding.saveSuccess"));
      setArchivedFields(result.archivedFields || []);
    } catch (e) {
      console.error("Error saving onboard config:", e);
      const apiError = (e as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      setError(apiError || t("countryOnboarding.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const baseLabel = (field: BaseFieldDefinition): string => {
    const label = field.labelKey ? t(field.labelKey) : "";
    return label && label !== field.labelKey ? label : field.fieldKey;
  };

  const visibleCustomFields = customFields.filter(
    (f) => f.status !== "archived",
  );
  /**
   * The base fields this screen can change.
   *
   * A mandatory-locked field is always visible and always required, in every
   * country, and the builder has nothing to offer for it - so it is not listed.
   * It stays in the catalog because the create and edit forms read the full
   * merged schema; it is only absent from the configuration UI.
   */
  const configurableBaseFields = catalog.filter((f) => f.configurable);
  const configurableBaseCount = configurableBaseFields.length;
  const hasConfig = !loading && selectedCountryId !== "" && catalog.length > 0;

  const typeChip = (type: string) => (
    <Chip
      size="small"
      label={t(`countryOnboarding.fieldTypes.${type}`)}
      sx={{
        height: 22,
        fontSize: "11.5px",
        fontWeight: 500,
        backgroundColor: CHIP_BG,
        color: CHIP_TEXT,
      }}
    />
  );

  /** Compact input used inside the aligned table rows. */
  const cellFieldSx = {
    "& .MuiOutlinedInput-root": {
      height: 34,
      fontSize: "13px",
      backgroundColor: "#fff",
    },
  };
  const cellSelectSx = {
    height: 34,
    fontSize: "12.5px",
    backgroundColor: "#fff",
  };

  /** Company-type targeting picker, shared by existing rows and the add row. */
  const renderAppliesTo = (
    value: FieldAppliesTo,
    onChange: (next: FieldAppliesTo) => void,
  ) => (
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value as FieldAppliesTo)}
      sx={cellSelectSx}
    >
      {FIELD_APPLIES_TO_VALUES.map((v) => (
        <MenuItem key={v} value={v}>
          {t(`countryOnboarding.appliesToValues.${v}`)}
        </MenuItem>
      ))}
    </Select>
  );

  /**
   * Uniqueness toggle.
   *
   * Only text and number can carry one - a date, a checkbox or a value picked
   * from a short dropdown is shared by design, and the API rejects the flag on
   * those. Rather than hide the control and leave a ragged column, it is shown
   * disabled with the reason on hover, so the rule is visible instead of
   * mysterious.
   */
  const renderUniqueToggle = (
    fieldType: CustomFieldType,
    value: boolean,
    onChange: (next: boolean) => void,
  ) => {
    const allowed = supportsUniqueness(fieldType);
    return (
      <Tooltip
        title={
          allowed
            ? (t("countryOnboarding.uniqueHint") as string)
            : (t("countryOnboarding.uniqueNotSupported") as string)
        }
      >
        <Box component="span" sx={{ display: "inline-flex" }}>
          <Switch
            size="small"
            disabled={!allowed}
            checked={allowed && value}
            onChange={(e) => onChange(e.target.checked)}
          />
        </Box>
      </Tooltip>
    );
  };

  /**
   * Business-type targeting picker. An empty selection means the field applies
   * to every business type, which is why the placeholder reads "All".
   */
  const renderBusinessTypes = (
    value: number[],
    onChange: (next: number[]) => void,
  ) => (
    <Select
      size="small"
      multiple
      displayEmpty
      value={value}
      onChange={(e) => onChange(e.target.value as number[])}
      renderValue={(selected) => {
        const ids = selected as number[];
        if (ids.length === 0) return t("countryOnboarding.allBusinessTypes");
        return businessTypes
          .filter((bt) => ids.includes(bt.businessTypeId))
          .map((bt) => bt.businessTypeName)
          .join(", ");
      }}
      sx={cellSelectSx}
    >
      {businessTypes.map((bt) => (
        <MenuItem key={bt.businessTypeId} value={bt.businessTypeId}>
          <Checkbox size="small" checked={value.includes(bt.businessTypeId)} />
          <ListItemText
            primary={bt.businessTypeName}
            primaryTypographyProps={{ fontSize: "13px" }}
          />
        </MenuItem>
      ))}
    </Select>
  );

  /**
   * Inline spinner for a cascading select while its options load.
   *
   * The select stays disabled with its "Loading..." placeholder, but a bare
   * disabled dropdown reads as "nothing here" rather than "fetching" - so a
   * small spinner sits where the chevron is, the same cue the breadcrumb
   * drill-down gives. Rendered as an endAdornment so it does not shift the
   * select's layout. `null` when idle, so the chevron returns once loaded.
   */
  const loadingAdornment = (isLoading: boolean) =>
    isLoading ? (
      <InputAdornment position="end" sx={{ mr: 2 }}>
        <CircularProgress size={16} thickness={5} />
      </InputAdornment>
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1180px, 96vw)",
          height: "90vh",
          maxHeight: "90vh",
          borderRadius: "14px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.75,
          px: 3,
          py: 2,
          borderBottom: `1px solid ${ROW_BORDER}`,
          flex: "none",
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            flex: "none",
            borderRadius: "10px",
            backgroundColor: CHIP_BG,
            color: ACCENT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <TuneIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "17px", fontWeight: 600, color: "#101828" }}>
            {t("countryOnboarding.title")}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: MUTED, mt: "2px" }}>
            {t("countryOnboarding.defaultFieldsHint")}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Toolbar: country picker + counts */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          px: 3,
          py: 1.5,
          borderBottom: `1px solid ${ROW_BORDER}`,
          flex: "none",
        }}
      >
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#344054" }}>
          {t("countryOnboarding.selectCountry")}
        </Typography>
        <Select
          size="small"
          value={selectedRegionId}
          displayEmpty
          disabled={regionsLoading}
          onChange={(e) => setSelectedRegionId(Number(e.target.value))}
          endAdornment={loadingAdornment(regionsLoading)}
          sx={{ minWidth: 170, height: 36, fontSize: "13px" }}
        >
          <MenuItem value="" disabled>
            {regionsLoading
              ? t("countryOnboarding.loadingCountries")
              : t("countryOnboarding.selectRegion")}
          </MenuItem>
          {regionList.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={selectedFrontlineId}
          displayEmpty
          disabled={selectedRegionId === "" || frontlinesLoading}
          onChange={(e) => setSelectedFrontlineId(Number(e.target.value))}
          endAdornment={loadingAdornment(frontlinesLoading)}
          sx={{ minWidth: 190, height: 36, fontSize: "13px" }}
        >
          <MenuItem value="" disabled>
            {frontlinesLoading
              ? t("countryOnboarding.loadingCountries")
              : t("countryOnboarding.selectFrontline")}
          </MenuItem>
          {frontlineList.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={selectedCountryId}
          displayEmpty
          disabled={selectedFrontlineId === "" || countriesLoading}
          onChange={(e) => setSelectedCountryId(Number(e.target.value))}
          endAdornment={loadingAdornment(countriesLoading)}
          sx={{ minWidth: 210, height: 36, fontSize: "13px" }}
        >
          <MenuItem value="" disabled>
            {countriesLoading
              ? t("countryOnboarding.loadingCountries")
              : selectedFrontlineId !== "" && countryList.length === 0
                ? t("countryOnboarding.noCountries")
                : t("countryOnboarding.selectCountry")}
          </MenuItem>
          {countryList.map((c) => (
            <MenuItem key={c.country_id} value={c.country_id}>
              {c.name}
            </MenuItem>
          ))}
        </Select>

        {hasConfig && (
          <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
            <Typography sx={{ fontSize: "13px", color: "#475467" }}>
              <b>{configurableBaseCount}</b>{" "}
              {t("countryOnboarding.configurableCount")}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#475467" }}>
              <b>{visibleCustomFields.length}</b>{" "}
              {t("countryOnboarding.customCount")}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Body - the only scrolling region */}
      <Box
        ref={bodyRef}
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2 }}
      >
        {(error || success || archivedFields.length > 0) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" onClose={() => setSuccess("")}>
                {success}
              </Alert>
            )}
            {archivedFields.length > 0 && (
              <Alert severity="warning" onClose={() => setArchivedFields([])}>
                {t("countryOnboarding.archivedNotice", {
                  fields: archivedFields.join(", "),
                })}
              </Alert>
            )}
          </Box>
        )}

        {loading && <Loading />}

        {!loading && selectedCountryId === "" && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: "13.5px", color: MUTED }}>
              {t("countryOnboarding.selectCountryPrompt")}
            </Typography>
          </Box>
        )}

        {hasConfig && (
          <>
            {/* ---------------- Default / base fields ---------------- */}
            <Typography sx={{ ...sectionTitleSx, mb: 1 }}>
              {t("countryOnboarding.defaultFields")}
            </Typography>
            <Box
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: "10px",
                overflow: "hidden",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: BASE_COLS,
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  height: 38,
                  backgroundColor: HEAD_BG,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.columns.field")}
                </Typography>
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.columns.type")}
                </Typography>
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.visible")}
                </Typography>
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.mandatory")}
                </Typography>
              </Box>

              {/*
                Only the fields this screen can actually change. A
                mandatory-locked field (Status, and the identity fields) has no
                switch to offer and cannot be hidden or made optional, so listing
                it was a row of dashes the admin had to read past on every visit.
                It is still enforced everywhere - it is simply not a decision
                that belongs on a configuration screen.
              */}
              {configurableBaseFields.map((field) => (
                <Box
                  key={field.fieldKey}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: BASE_COLS,
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    minHeight: 44,
                    borderBottom: `1px solid ${ROW_BORDER}`,
                    "&:last-of-type": { borderBottom: "none" },
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography sx={{ fontSize: "13.5px", color: "#1f2933" }}>
                    {baseLabel(field)}
                  </Typography>
                  <Box>{typeChip(field.dataType)}</Box>
                  <Switch
                    size="small"
                    checked={!!baseState[field.fieldKey]?.isVisible}
                    onChange={() => toggleBase(field.fieldKey, "isVisible")}
                  />
                  <Switch
                    size="small"
                    checked={!!baseState[field.fieldKey]?.isMandatory}
                    disabled={!baseState[field.fieldKey]?.isVisible}
                    onChange={() => toggleBase(field.fieldKey, "isMandatory")}
                  />
                </Box>
              ))}
            </Box>

            {/* ---------------- Custom fields ---------------- */}
            <Typography sx={{ ...sectionTitleSx, mb: 1 }}>
              {t("countryOnboarding.customFields")}
            </Typography>
            <Box
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: CUSTOM_COLS,
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  height: 38,
                  backgroundColor: HEAD_BG,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.columns.fieldName")}
                </Typography>
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.columns.type")}
                </Typography>
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.columns.limitOptions")}
                </Typography>
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.columns.appliesTo")}
                </Typography>
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.columns.businessTypes")}
                </Typography>
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.mandatory")}
                </Typography>
                <Typography sx={columnHeadSx}>
                  {t("countryOnboarding.columns.unique")}
                </Typography>
                <Box />
              </Box>

              {visibleCustomFields.length === 0 && (
                <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "13px", color: MUTED }}>
                    {t("countryOnboarding.noCustomFields")}
                  </Typography>
                </Box>
              )}

              {visibleCustomFields.map((field) => (
                <Box
                  key={field.columnName}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: CUSTOM_COLS,
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    minHeight: 52,
                    borderBottom: `1px solid ${ROW_BORDER}`,
                  }}
                >
                  <TextField
                    size="small"
                    value={field.displayName}
                    inputProps={{ maxLength: MAX_FIELD_NAME_LENGTH }}
                    onChange={(e) =>
                      updateCustomField(field.columnName, {
                        displayName: e.target.value,
                      })
                    }
                    sx={cellFieldSx}
                  />
                  {/* The column name is fixed once created - it is the physical
                      column. Changing a populated column's type is refused by the
                      API, so the type is locked for persisted fields. */}
                  <Box>{typeChip(field.fieldType)}</Box>
                  <Box>
                    {field.fieldType === "text" && (
                      <TextField
                        size="small"
                        type="number"
                        placeholder={t("countryOnboarding.charLimit") as string}
                        value={field.charLimit ?? ""}
                        onChange={(e) =>
                          updateCustomField(field.columnName, {
                            charLimit: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                        sx={{ ...cellFieldSx, width: 110 }}
                      />
                    )}
                    {field.fieldType === "dropdown" && (
                      <TextField
                        size="small"
                        fullWidth
                        placeholder={t("countryOnboarding.optionsHint") as string}
                        value={(field.fieldOptions || []).join(", ")}
                        onChange={(e) =>
                          updateCustomField(field.columnName, {
                            fieldOptions: parseOptions(e.target.value),
                          })
                        }
                        sx={cellFieldSx}
                      />
                    )}
                    {field.fieldType !== "text" &&
                      field.fieldType !== "dropdown" && (
                        <Typography sx={{ fontSize: "13px", color: "#98a2b3" }}>
                          &mdash;
                        </Typography>
                      )}
                  </Box>
                  {renderAppliesTo(field.appliesTo ?? "all", (next) =>
                    updateCustomField(field.columnName, { appliesTo: next }),
                  )}
                  {renderBusinessTypes(field.businessTypeIds ?? [], (next) =>
                    updateCustomField(field.columnName, {
                      businessTypeIds: next,
                    }),
                  )}
                  <Switch
                    size="small"
                    checked={!!field.isMandatory}
                    onChange={(e) =>
                      updateCustomField(field.columnName, {
                        isMandatory: e.target.checked,
                      })
                    }
                  />
                  {renderUniqueToggle(field.fieldType, !!field.isUnique, (next) =>
                    updateCustomField(field.columnName, { isUnique: next }),
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveField(field.columnName)}
                    aria-label="remove"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              {/* Add-field row, aligned to the same columns as the rows above */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: CUSTOM_COLS,
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  minHeight: 60,
                  backgroundColor: "#fbfcfd",
                }}
              >
                <TextField
                  size="small"
                  placeholder={t("countryOnboarding.fieldName") as string}
                  value={draft.displayName}
                  // Capped in the input as well as on submit: this name becomes
                  // a database column, so the limit is worth hitting while
                  // typing rather than after pressing Add.
                  inputProps={{ maxLength: MAX_FIELD_NAME_LENGTH }}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, displayName: e.target.value }))
                  }
                  sx={cellFieldSx}
                />
                <Select
                  size="small"
                  value={draft.fieldType}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      fieldType: e.target.value as CustomFieldType,
                    }))
                  }
                  sx={{ height: 34, fontSize: "13px", backgroundColor: "#fff" }}
                >
                  {FIELD_TYPES.map((ft) => (
                    <MenuItem key={ft} value={ft}>
                      {t(`countryOnboarding.fieldTypes.${ft}`)}
                    </MenuItem>
                  ))}
                </Select>
                <Box>
                  {/* Only text fields have a configurable width: number is
                      DECIMAL(20,4) and dropdown is a fixed VARCHAR server-side. */}
                  {draft.fieldType === "text" && (
                    <TextField
                      size="small"
                      type="number"
                      placeholder={t("countryOnboarding.charLimit") as string}
                      value={draft.charLimit}
                      onChange={(e) =>
                        setDraft((p) => ({ ...p, charLimit: e.target.value }))
                      }
                      sx={{ ...cellFieldSx, width: 110 }}
                    />
                  )}
                  {draft.fieldType === "dropdown" && (
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={t("countryOnboarding.optionsHint") as string}
                      value={draft.fieldOptions}
                      onChange={(e) =>
                        setDraft((p) => ({ ...p, fieldOptions: e.target.value }))
                      }
                      sx={cellFieldSx}
                    />
                  )}
                </Box>
                {renderAppliesTo(draft.appliesTo, (next) =>
                  setDraft((p) => ({ ...p, appliesTo: next })),
                )}
                {renderBusinessTypes(draft.businessTypeIds, (next) =>
                  setDraft((p) => ({ ...p, businessTypeIds: next })),
                )}
                <Switch
                  size="small"
                  checked={draft.isMandatory}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, isMandatory: e.target.checked }))
                  }
                />
                {renderUniqueToggle(draft.fieldType, draft.isUnique, (next) =>
                  setDraft((p) => ({ ...p, isUnique: next })),
                )}
                <IconButton
                  size="small"
                  onClick={handleAddField}
                  aria-label="add"
                  sx={{ color: ACCENT }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {draftError && (
              <Typography
                sx={{ fontSize: "12.5px", color: "#b42318", mt: 1, ml: 0.5 }}
              >
                {draftError}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Sticky footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
          px: 3,
          py: 2,
          borderTop: `1px solid ${ROW_BORDER}`,
          flex: "none",
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <StyledButtons.SecondaryButton onClick={onClose} disabled={saving}>
            {t("countryOnboarding.cancel")}
          </StyledButtons.SecondaryButton>
          <StyledButtons.PrimaryButton
            onClick={handleSave}
            disabled={selectedCountryId === "" || loading || saving}
          >
            {t("countryOnboarding.saveAndOnboard")}
          </StyledButtons.PrimaryButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CountryOnboarding;
